import React, { FC } from "react";
import { Box, LinearProgress, SxProps, Theme, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../Constants/colors";

// Building blocks of the profile page, styled like the sidebar.

const buttonVariants = () => ({
  primary: {
    backgroundColor: color.primary_dark,
    color: color.white,
    border: `1px solid ${color.primary_dark}`,
    "&:hover": { backgroundColor: color.primary_dark, opacity: 0.9 },
  },
  outlined: {
    backgroundColor: color.white,
    color: color.primary_dark,
    border: `1px solid ${color.gray300}`,
    "&:hover": { backgroundColor: color.gray50, borderColor: color.gray400 },
  },
  danger: {
    backgroundColor: color.red600,
    color: color.white,
    border: `1px solid ${color.red600}`,
    "&:hover": { backgroundColor: color.red700, borderColor: color.red700 },
  },
});

export const profileButtonSx = (
  variant: "primary" | "outlined" | "danger" = "outlined",
) => ({
  flexShrink: 0,
  height: 38,
  px: 2,
  borderRadius: "10px",
  textTransform: "none" as const,
  fontSize: 14,
  fontWeight: 600,
  whiteSpace: "nowrap" as const,
  boxShadow: "none",
  ...buttonVariants()[variant],
  "&.Mui-disabled": { opacity: 0.6, color: "inherit" },
});

// Tables on the profile pages. The theme paints table cells white for the
// dark layout, so text and background colours are set explicitly.
export const profileTableContainerSx = () => ({
  border: `1px solid ${color.gray200}`,
  borderRadius: "12px",
  backgroundColor: color.white,
  overflow: "auto",
});

export const profileHeadCellSx = () => ({
  backgroundColor: `${color.gray50} !important`,
  color: `${color.gray600} !important`,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap" as const,
  borderBottom: `1px solid ${color.gray200} !important`,
  py: 1.25,
});

export const profileBodyCellSx = () => ({
  color: `${color.primary_dark} !important`,
  fontSize: 13.5,
  borderBottom: `1px solid ${color.gray100} !important`,
  py: 1.25,
});

export const profileTableRowSx = () => ({
  "&:hover .MuiTableCell-root": {
    backgroundColor: `${color.gray50} !important`,
  },
  "&:last-of-type .MuiTableCell-root": { borderBottom: "none !important" },
});

export const profileEmptyCellSx = () => ({
  ...profileBodyCellSx(),
  color: `${color.gray500} !important`,
  borderBottom: "none !important",
  py: 3,
  textAlign: "center" as const,
});

export type StatusPillTone = "success" | "danger" | "info" | "neutral";

const pillColors = (tone: StatusPillTone) =>
  ({
    success: { bg: color.green50, fg: color.green700 },
    danger: { bg: color.red50, fg: color.red700 },
    info: { bg: color.blue50, fg: color.blue700 },
    neutral: { bg: color.gray100, fg: color.gray700 },
  })[tone];

// Small label for states and facts: profile chips, document status, etc.
export const StatusPill: FC<{
  tone: StatusPillTone;
  children: React.ReactNode;
}> = ({ tone, children }) => {
  const { bg, fg } = pillColors(tone);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        maxWidth: "100%",
        px: 1,
        py: 0.25,
        borderRadius: "6px",
        fontSize: 12,
        fontWeight: 600,
        lineHeight: "18px",
        backgroundColor: bg,
        color: fg,
        "& .MuiSvgIcon-root": { fontSize: 15 },
      }}
    >
      {children}
    </Box>
  );
};

export const ProfileIconBox: FC<{
  children: React.ReactNode;
  tone?: "default" | "warning" | "danger";
}> = ({ children, tone = "default" }) => (
  <Box
    sx={{
      width: 40,
      height: 40,
      flexShrink: 0,
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor:
        tone === "warning"
          ? color.orange50
          : tone === "danger"
            ? color.red50
            : color.gray100,
      color:
        tone === "warning"
          ? color.orange600
          : tone === "danger"
            ? color.red600
            : color.primary_dark,
      "& .MuiSvgIcon-root": { fontSize: 22 },
    }}
  >
    {children}
  </Box>
);

interface CardProps {
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const ProfileCard: FC<CardProps> = ({
  title,
  subtitle,
  icon,
  action,
  children,
  sx,
}) => (
  <Box
    sx={[
      {
        backgroundColor: color.white,
        border: `1px solid ${color.gray200}`,
        borderRadius: "16px",
        p: { xs: 2, sm: 2.5 },
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {title && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: children ? 2.5 : 0,
        }}
      >
        {icon && <ProfileIconBox>{icon}</ProfileIconBox>}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              lineHeight: "22px",
              color: color.primary_dark,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{ fontSize: 13, lineHeight: "18px", color: color.gray500 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

interface SettingRowProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  tone?: "default" | "warning" | "danger";
}

export const SettingRow: FC<SettingRowProps> = ({
  icon,
  title,
  description,
  action,
  tone = "default",
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: { xs: "flex-start", sm: "center" },
      flexDirection: { xs: "column", sm: "row" },
      gap: { xs: 1.5, sm: 2 },
      p: tone === "default" ? 0 : 1.5,
      borderRadius: "12px",
      backgroundColor:
        tone === "warning"
          ? color.orange50
          : tone === "danger"
            ? color.red50
            : "transparent",
      border:
        tone === "warning"
          ? `1px solid ${color.orange200}`
          : tone === "danger"
            ? `1px solid ${color.red200}`
            : "none",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        flex: 1,
        minWidth: 0,
      }}
    >
      <ProfileIconBox tone={tone}>{icon}</ProfileIconBox>
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
        <Typography
          component="div"
          sx={{
            fontSize: 14.5,
            fontWeight: 600,
            lineHeight: "20px",
            color:
              tone === "danger"
                ? color.red700
                : tone === "warning"
                  ? color.orange600
                  : color.primary_dark,
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            component="div"
            sx={{
              mt: 0.25,
              fontSize: 13,
              lineHeight: "19px",
              color: tone === "danger" ? color.red700 : color.gray500,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Box>
    {action && (
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          pl: { xs: 6.5, sm: 0 },
        }}
      >
        {action}
      </Box>
    )}
  </Box>
);

// Used against a limit, with the percentage below; null limit means unlimited.
export const UsageMeter: React.FC<{
  label: string;
  value: number | string;
  limit: number | null;
  used: number;
}> = ({ label, value, limit, used }) => {
  const { t } = useTranslation("profile");
  // null means unlimited; a limit of 0 means nothing is allowed.
  const hasLimit = limit !== null;
  const percent =
    limit === null
      ? 0
      : limit === 0
        ? 100
        : Math.min((used / limit) * 100, 100);
  const barColor =
    percent >= 100
      ? color.red600
      : percent >= 80
        ? color.orange500
        : color.accent;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 2,
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: 13.5, color: color.gray600 }}>
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 700,
            color: color.primary_dark,
            whiteSpace: "nowrap",
          }}
        >
          {value}
          <Box
            component="span"
            sx={{ fontWeight: 500, color: color.gray400, ml: 0.5 }}
          >
            /{" "}
            {hasLimit ? limit.toLocaleString("de-DE") : t("profile.unlimited")}
          </Box>
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percent}
        aria-label={label}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: color.gray100,
          "& .MuiLinearProgress-bar": {
            borderRadius: 4,
            backgroundColor: barColor,
          },
        }}
      />
      <Typography
        sx={{
          mt: 0.75,
          fontSize: 12,
          color: color.gray500,
          textAlign: "right",
        }}
      >
        {hasLimit ? `${percent.toFixed(0)}%` : t("profile.unlimited")}
      </Typography>
    </Box>
  );
};
